import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Upload, Trash2, Info } from "lucide-react";
import { API } from "@/lib/brand";
import { JOURNAL_CATEGORIES, resolveImageUrl } from "@/lib/journal";
import { FEATURED_PRESETS } from "@/lib/journal-presets";
import TipTapEditor from "@/components/journal/TipTapEditor";
import PresetHero from "@/components/journal/PresetHero";

const emptyPost = {
  title: "",
  slug: "",
  category: "goals",
  excerpt: "",
  body: "",
  featured_image_path: null,
  featured_preset: null,
  status: "draft",
  scheduled_for: null,
};

function toLocalDatetime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIsoUtc(local) {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function AdminEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [post, setPost] = useState(emptyPost);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [scheduledLocal, setScheduledLocal] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const res = await fetch(`${API}/journal/admin/posts/${id}`, { credentials: "include" });
        if (!res.ok) throw new Error("Not found");
        const data = await res.json();
        setPost({ ...emptyPost, ...data });
        setScheduledLocal(toLocalDatetime(data.scheduled_for));
      } catch {
        toast.error("Could not load post");
        navigate("/journal/admin");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (patch) => setPost((p) => ({ ...p, ...patch }));

  const upload = async (file) => {
    setImgUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API}/journal/admin/upload`, {
        method: "POST", credentials: "include", body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Upload failed");
      }
      const data = await res.json();
      set({ featured_image_path: data.storage_path });
      toast.success("Featured image uploaded");
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setImgUploading(false);
    }
  };

  const save = async (statusOverride) => {
    const targetStatus = statusOverride || post.status;
    if (!post.title.trim()) {
      toast.error("Title is required");
      return;
    }
    let scheduledIso = null;
    if (targetStatus === "scheduled") {
      scheduledIso = toIsoUtc(scheduledLocal);
      if (!scheduledIso) { toast.error("Pick a valid schedule date/time"); return; }
      if (new Date(scheduledIso) <= new Date()) {
        toast.error("Schedule must be in the future");
        return;
      }
    }
    const payload = {
      title: post.title,
      slug: post.slug || null,
      category: post.category,
      excerpt: post.excerpt || "",
      body: post.body || "",
      featured_image_path: post.featured_image_path || null,
      featured_preset: post.featured_image_path ? null : (post.featured_preset || null),
      status: targetStatus,
      scheduled_for: scheduledIso,
    };
    setSaving(true);
    try {
      const url = isNew ? `${API}/journal/admin/posts` : `${API}/journal/admin/posts/${id}`;
      const res = await fetch(url, {
        method: isNew ? "POST" : "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Save failed");
      }
      const data = await res.json();
      toast.success(isNew ? "Post created" : "Changes saved");
      if (isNew) navigate(`/journal/admin/edit/${data.id}`, { replace: true });
      else setPost({ ...emptyPost, ...data });
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const featuredImg = useMemo(
    () => resolveImageUrl(post.featured_image_path ? `/api/journal/image/${post.featured_image_path}` : null),
    [post.featured_image_path]
  );

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-ink-soft">
        <Loader2 size={22} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-8 py-12">
      <div className="mb-6">
        <Link to="/journal/admin" className="link-brass inline-flex items-center gap-1.5 text-[13px]">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="eyebrow mb-2">{isNew ? "New post" : "Edit post"}</div>
          <h1 className="font-display text-3xl text-ink">
            {post.title || "Untitled"}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end">
          <button onClick={() => save("draft")} className="btn-outline" disabled={saving} data-testid="save-draft">
            Save as draft
          </button>
          <button onClick={() => save(post.status === "scheduled" ? "scheduled" : "published")} className="btn-brass" disabled={saving} data-testid="save-publish">
            {saving ? <Loader2 size={16} className="animate-spin" /> : null}
            {post.status === "scheduled" ? "Save schedule" : "Publish"}
          </button>
        </div>
      </div>

      {/* Editorial self-check */}
      <div className="card-paper p-5 mb-8 border-l-2 border-brass" data-testid="editorial-checklist">
        <div className="flex items-start gap-3">
          <Info size={16} className="text-brass shrink-0 mt-1" />
          <div className="text-[13.5px] text-ink-soft leading-relaxed">
            <strong className="text-ink block mb-1.5">Before you publish — a quick self-check:</strong>
            Does this post frame investment discussion as <em>mutual funds</em> (equity, debt, gold,
            hybrid) — not direct equity, real estate, or commodities?
            Are insurance and investment kept separate — no policy described as a growth vehicle?
            Are all return or inflation figures marked <em>illustrative</em>, not guaranteed or
            personalized?
          </div>
        </div>
      </div>

      {/* Meta grid */}
      <div className="grid md:grid-cols-2 gap-5 mb-6">
        <div className="md:col-span-2">
          <label className="block">
            <span className="eyebrow block mb-2">Title</span>
            <input
              className="fld"
              value={post.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="e.g. Why every SIP needs a purpose"
              data-testid="post-title-input"
            />
          </label>
        </div>

        <label className="block">
          <span className="eyebrow block mb-2">Slug (URL)</span>
          <input
            className="fld num"
            value={post.slug}
            onChange={(e) => set({ slug: e.target.value })}
            placeholder="auto from title"
            data-testid="post-slug-input"
          />
        </label>

        <label className="block">
          <span className="eyebrow block mb-2">Category</span>
          <select
            className="fld"
            value={post.category}
            onChange={(e) => set({ category: e.target.value })}
            data-testid="post-category-select"
          >
            {JOURNAL_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>

        <div className="md:col-span-2">
          <label className="block">
            <span className="eyebrow block mb-2">Excerpt / summary</span>
            <textarea
              className="fld min-h-[80px] resize-y"
              value={post.excerpt}
              onChange={(e) => set({ excerpt: e.target.value })}
              placeholder="One or two sentences that show up on cards and previews."
              data-testid="post-excerpt-input"
            />
          </label>
        </div>

        <div className="md:col-span-2">
          <span className="eyebrow block mb-2">Featured image</span>
          <div className="flex items-start gap-4">
            {featuredImg ? (
              <div className="w-40 aspect-[16/9] overflow-hidden rounded-md border hairline shrink-0">
                <img src={featuredImg} alt="Featured" className="w-full h-full object-cover" />
              </div>
            ) : post.featured_preset ? (
              <div className="w-40 aspect-[16/9] overflow-hidden rounded-md border hairline shrink-0">
                <PresetHero presetId={post.featured_preset} title={post.title || "Preview"} showCaption={false} />
              </div>
            ) : (
              <div className="w-40 aspect-[16/9] rounded-md border hairline bg-paper-raised flex items-center justify-center text-ink-soft text-[12px] shrink-0">
                No image
              </div>
            )}
            <div className="flex flex-col gap-2">
              <input
                type="file"
                accept="image/*"
                ref={fileRef}
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
                data-testid="featured-image-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="btn-outline text-[13px]"
                disabled={imgUploading}
                data-testid="featured-image-upload"
              >
                {imgUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {featuredImg ? "Replace" : "Upload"}
              </button>
              {featuredImg && (
                <button
                  type="button"
                  onClick={() => set({ featured_image_path: null })}
                  className="btn-outline text-[13px]"
                >
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
          </div>

          {/* Preset picker — only relevant when no custom image is set */}
          {!featuredImg && (
            <div className="mt-5">
              <div className="eyebrow mb-2">Or pick a branded preset</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="preset-picker">
                {FEATURED_PRESETS.map((p) => {
                  const active = post.featured_preset === p.id;
                  return (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => set({ featured_preset: active ? null : p.id })}
                      className={`aspect-[16/9] rounded-md overflow-hidden border-2 transition-colors ${active ? "border-brass-deep" : "border-line hover:border-ink"}`}
                      title={p.label}
                      data-testid={`preset-${p.id}`}
                    >
                      <PresetHero presetId={p.id} title="" showCaption={false} />
                    </button>
                  );
                })}
              </div>
              {post.featured_preset && (
                <button
                  type="button"
                  onClick={() => set({ featured_preset: null })}
                  className="link-brass text-[12.5px] mt-3 inline-flex items-center gap-1"
                  data-testid="clear-preset"
                >
                  <Trash2 size={12} /> Clear preset
                </button>
              )}
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <span className="eyebrow block mb-2">Publish state</span>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="publish-state-tabs">
            {["draft", "published", "scheduled"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => set({ status: s })}
                className={`px-4 py-1.5 rounded-full text-[12.5px] border transition-colors ${
                  post.status === s
                    ? "bg-ink text-paper border-ink"
                    : "border-line text-ink-soft hover:text-ink hover:border-ink"
                }`}
                data-testid={`state-${s}`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          {post.status === "scheduled" && (
            <label className="block max-w-sm">
              <span className="eyebrow block mb-2">Publish at</span>
              <input
                type="datetime-local"
                className="fld num"
                value={scheduledLocal}
                onChange={(e) => setScheduledLocal(e.target.value)}
                data-testid="scheduled-datetime"
              />
              <p className="text-[11px] text-ink-soft mt-1.5">
                Post will go live automatically at this local time.
              </p>
            </label>
          )}
        </div>
      </div>

      <span className="eyebrow block mb-2">Body</span>
      <TipTapEditor value={post.body} onChange={(html) => set({ body: html })} />
    </div>
  );
}
